import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import React from "react";
import useApi from "../../hooks/useGaneshApi";
import { useError } from "../../contexts/ErrorContext";
import { useQuery } from "@tanstack/react-query";

import { DeliveryHistoryRecord_T } from "../../types/Delivery";

import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import { getDateValue, renderDateCell } from "utils/dataGrid";
import TableCellBooleanIcon from "../UI/TableCellBooleanIcon";

/*
  This component takes in a delivery id, and returns a data grid with the future delivery schedule
*/

interface DeliveryScheduleTableProps {
  deliveryId?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "target_fulfillment_date", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const columns_def: GridColDef[] = [
  {
    field: "target_fulfillment_date",
    headerName: "Target Fulfillment Date",
    width: 160,
    type: "date",
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "volume_upgrade_scheduled",
    headerName: "Volume Upgrade Scheduled",
    width: 160,
    type: "boolean",
    renderCell: (params) => <TableCellBooleanIcon value={params.value} />,
  },
  {
    field: "is_ad_hoc",
    headerName: "Is Ad-Hoc",
    width: 160,
    type: "boolean",
    renderCell: (params) => <TableCellBooleanIcon value={params.value} />,
  },
];

const DeliveryScheduleTable: React.FC<DeliveryScheduleTableProps> = ({
  deliveryId,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();
  const { data, isLoading, isError, error } = useQuery<
    DeliveryHistoryRecord_T[]
  >({
    queryKey: ["delivery-schedule", deliveryId],
    queryFn: async () => {
      const response = await apiRequest<DeliveryHistoryRecord_T[]>(
        `/v1/deliveries/${deliveryId}/future_schedule`,
        { method: "GET" }
      );
      return response.data || [];
    },
    enabled: !!deliveryId,
  });

  if (isError) {
    showError(`Failed to fetch delivery schedule: ${error.message}`);
  }

  return (
    <GaneshDataGrid
      loading={isLoading}
      data={data}
      columns={columns_def}
      gridId="deliveryScheduleGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default DeliveryScheduleTable;
